/**
 * Kotlin struct generator - Functional implementation with dependency injection
 * Generates Kotlin data classes from parsed module definitions
 */

import type { InterfaceDefinition, ParsedModule } from "../../parser";
import type { KotlinCodeEmitter } from "./kotlin-code-emitter";
import type { KotlinTypeMapper } from "./kotlin-type-mapper";

export interface KotlinStructGeneratorDeps {
  typeMapper: KotlinTypeMapper;
  codeEmitter: KotlinCodeEmitter;
}

export interface KotlinStructGenerator {
  /**
   * Generates all Kotlin types for a module (library mode)
   */
  generateModuleTypes(module: ParsedModule): string;

  /**
   * Generates Kotlin data class for library mode
   */
  generateLibraryKotlinDataClass(interfaceDef: InterfaceDefinition): string;

  /**
   * Generates Kotlin data class from TypeScript interface definition (AST-based)
   */
  generateKotlinDataClassFromInterface(interfaceDef: {
    name: string;
    properties: Array<{ name: string; type: string; optional: boolean }>;
  }): string;

  /**
   * Generates a simple Kotlin data class from inline object type (fallback)
   */
  generateKotlinDataClass(className: string): string;
}

/**
 * Creates a Kotlin struct generator with injected dependencies
 */
export function createKotlinStructGenerator(
  deps: KotlinStructGeneratorDeps,
): KotlinStructGenerator {
  const { typeMapper, codeEmitter } = deps;

  /**
   * Generates all Kotlin types for a module (library mode)
   */
  function generateModuleTypes(module: ParsedModule): string {
    // Generate Kotlin data classes for interface definitions from AST
    const interfaceClasses = module.interfaceDefinitions
      .map((interfaceDef) => generateLibraryKotlinDataClass(interfaceDef))
      .join("\n\n");

    // Get Android package info from scanned module
    const androidPackage =
      module.sourceModule?.brickConfig?.android?.package ||
      module.sourceModule?.androidPackage ||
      "com.brickmodule.codegen";

    return codeEmitter.generateKotlinFile(
      androidPackage,
      ["com.google.gson.annotations.SerializedName"],
      `/**
 * ${module.moduleName} module types for Brick framework
 * Provides type-safe data classes with automatic serialization
 */

${interfaceClasses}`,
    );
  }

  /**
   * Generates Kotlin data class for library mode
   */
  function generateLibraryKotlinDataClass(
    interfaceDef: InterfaceDefinition,
  ): string {
    const properties = interfaceDef.properties.map((prop) => {
      const kotlinType = typeMapper.mapTypeScriptToKotlinWithInterfaces(prop.type);
      const optional = prop.optional ? "?" : "";
      const comment = prop.jsDocComment ? prop.jsDocComment : undefined;

      return {
        name: prop.name,
        type: `${kotlinType}${optional}`,
        comment,
        serializedName: prop.name,
      };
    });

    const comment = interfaceDef.jsDocComment
      ? `/**\n * ${interfaceDef.jsDocComment}\n */\n`
      : "";

    return `${comment}${codeEmitter.generateKotlinDataClass(interfaceDef.name, properties, true)}`;
  }

  /**
   * Generates Kotlin data class from TypeScript interface definition (AST-based)
   */
  function generateKotlinDataClassFromInterface(interfaceDef: {
    name: string;
    properties: Array<{ name: string; type: string; optional: boolean }>;
  }): string {
    const properties = interfaceDef.properties.map((prop) => {
      let kotlinType = typeMapper.mapTypeScriptToKotlin(prop.type);

      if (prop.optional && !kotlinType.endsWith("?")) {
        kotlinType += "?";
      }

      return {
        name: prop.name,
        type: kotlinType + (prop.optional ? " = null" : ""),
        serializedName: prop.name,
      };
    });

    return codeEmitter.generateKotlinDataClass(
      interfaceDef.name,
      properties,
      true,
    );
  }

  /**
   * Generates a simple Kotlin data class from inline object type (fallback)
   * Note: This is a fallback for inline objects. Most types should come from AST-based interfaces.
   */
  function generateKotlinDataClass(className: string): string {
    console.warn(
      `[Android] Generating data class for inline object type: ${className}. Consider extracting as interface.`,
    );

    // Simple fallback implementation - in practice, the parser should extract all interfaces
    return codeEmitter.generateKotlinDataClass(
      className,
      [{ name: "data", type: "Any", serializedName: "data" }],
      true,
    );
  }

  return {
    generateModuleTypes,
    generateLibraryKotlinDataClass,
    generateKotlinDataClassFromInterface,
    generateKotlinDataClass,
  };
}
